import { CloseOutlined } from '@icon';
import { FileIcon } from '@illustrations';
import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import { useTheme } from 'styled-components';
import IconButton from '../icon-button';
import FilePreviewList from './file-preview-list';
import {
  FileMime,
  FileName,
  FileNameCenter,
  FilePreviewSC,
  IconWrapper,
  ImagePreview,
  getFileColor,
} from './file-preview.styled';
import { UploadFileStatus } from './file-preview.types';

interface FilePreviewProps
  extends Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    'ref'
  > {
  mimeType: string;
  fileName?: string;
  onDelete?: () => void;
  onPreview?: () => void;
  status?: UploadFileStatus;
  progress?: number;
  thumbnailUrl?: string;
}

const FilePreview = ({
  mimeType,
  fileName,
  onDelete,
  onPreview,
  className,
  status,
  progress,
  thumbnailUrl,
  ...rest
}: FilePreviewProps): JSX.Element => {
  const theme = useTheme();
  const [mime, ext] = mimeType.split('/');
  const fileColor = getFileColor(theme, mime);

  return (
    <FilePreviewSC
      className={classNames('ui-file-preview', className, `ui-file-preview-${status}`)}
      {...rest}
    >
      {thumbnailUrl ? (
        <ImagePreview src={thumbnailUrl} alt={fileName} />
      ) : (
        <IconWrapper>
          <FileIcon color={fileColor} width={38} height={42} />
          <FileMime noWrap display="captionSmall">
            {ext}
          </FileMime>
        </IconWrapper>
      )}
      <FileNameCenter>
        {fileName ? (
          <FileName
            display="body3"
            variant="strong"
            className={classNames('ui-file-preview-text', { clickable: !!onPreview })}
            onClick={onPreview}
            noWrap
          >
            {fileName}
          </FileName>
        ) : null}
        {status === 'uploading' && progress !== undefined ? (
          <div className="progress-track" style={{ width: '100%' }}>
            <div style={{ width: `${progress}%` }} className="progress" />
          </div>
        ) : null}
      </FileNameCenter>
      {onDelete ? (
        <IconButton
          size="small"
          Icon={CloseOutlined}
          style={{ marginLeft: 16 }}
          variant="ghost-secondary"
          onClick={onDelete}
        />
      ) : null}
    </FilePreviewSC>
  );
};

FilePreview.List = FilePreviewList;

export default hoistNonReactStatics(React.memo(FilePreview), FilePreview);
